import {PrAxleConfigurationType, PrRoadwayClassifications} from '@/modules/perRoad/interfaces/perRoad';

/**
 * Distribution of AADT percent based on vehicle classification.
 * Classifications include categories 4 - 13, in order.
 */

export const aadtDistByRoadClass: { [index: string]: number[] } = {
  [PrRoadwayClassifications.RURAL_INTERSTATE]: [1.2, 9.4, 3.3, 0.5, 7.4, 68.9, 1.2, 6.1, 0.8, 1.2],
  [PrRoadwayClassifications.RURAL_PRINCIPLE_ARTERIAL]: [2.56, 21.37, 6.84, 0.85, 12.82, 47.86, 3.42, 1.71, 0.85, 1.72],
  [PrRoadwayClassifications.RURAL_MINOR_ARTERIAL]: [3.5, 25.58, 8.14, 1.16, 18.6, 40.7, 1.16, 1.16, 0.0],
  [PrRoadwayClassifications.RURAL_MAJOR_COLLECTOR]: [5.6, 52.1, 8.5, 0, 12.7, 21.1, 0, 0, 0, 0],
  [PrRoadwayClassifications.RURAL_MINOR_COLLECTOR]: [5.5, 34.4, 12.6, 1.9, 13.4, 29.8, 1.6, 0, 0, 0.8],
  [PrRoadwayClassifications.RURAL_LOCAL_COLLECTOR]: [3.5, 48.4, 9.2, 0, 6.6, 29.1, 3.2, 0, 0, 0],
  [PrRoadwayClassifications.URBAN_INTERSTATE]: [2.4, 18.4, 9.2, 0, 12.6, 44.8, 2.3, 2.3, 1.1, 6.9],
  [PrRoadwayClassifications.URBAN_OTHER_FREEWAY]: [1.8, 22.7, 5.5, 2.7, 12.7, 35.5, 5.5, 2.7, 3.6, 7.3],
  [PrRoadwayClassifications.URBAN_PRINCIPLE_ARTERIAL]: [4.2, 20.8, 7.3, 1, 11.5, 46.9, 2.1, 3.1, 1, 2.1],
  [PrRoadwayClassifications.URBAN_MINOR_ARTERIAL]: [1.9, 34.5, 7.3, 0, 21.8, 23.6, 5.5, 0, 1.8, 3.6],
  [PrRoadwayClassifications.URBAN_COLLECTOR]: [1, 73.5, 11.5, 3.1, 4, 6.9, 0, 0, 0, 0],
};

/**
 * Load distributions are a map of lists. The map key is the vehicle classification from 4-13 and the list of numbers
 * represents the percent of vehicles with load values in the bucket. Each bucket represents an axle load range of 2
 * kip. Not all lists have the same number of entries, but stop at the end of the distribution. Therefore they may need
 * to be padded when generating load distributions.
 *
 * The first bucket is 0-2 kip, the second is 2-4 kip, etc.
 *
 * Ex:
 * {
 *   5: [0, 1, 2, 3]
 * }
 *
 * Represents vehicle class 5, 0% axles have 0-2 kip load, 1% are 2-4 kip, 2% are 4-6 kip, and 3% are 6-8kip.
 *
 *
 */

export const singleLoadDistributions = {
  4: [0, 1.8, 3.87, 10.79, 22.77, 20.85, 15.87, 9.78, 5.65, 3.51, 2.35, 1.22, 0.68, 0.36, 0.22, 0.09, 0.08, 0.05, 0.03, 0.02, 0.01],
  5: [0, 10.05, 29.63, 19.83, 15.39, 10.38, 6.02, 3.46, 2.09, 1.2, 0.71, 0.49, 0.29, 0.2, 0.1, 0.04, 0.04, 0.04, 0.03, 0.01],
  6: [0, 2.47, 5.23, 10.65, 20.3, 25.7, 16.31, 8.79, 4.58, 2.5, 1.46, 0.87, 0.5, 0.26, 0.16, 0.08, 0.06, 0.03, 0.02, 0.02, 0.01],
  7: [0, 2.14, 2.97, 5.91, 11.07, 14.24, 16.41, 15.92, 12.51, 8.13, 4.54, 2.28, 1.1, 1.44, 0.34, 0.8, 0.09, 0.05, 0.03, 0.02, 0.01],
  8: [0, 11.65, 13.21, 14.98, 19.56, 14.98, 9.18, 6.11, 4.02, 2.69, 1.58, 0.91, 0.45, 0.27, 0.13, 0.08, 0.13, 0.04, 0.02, 0.01],
  9: [0, 1.74, 4.21, 8.46, 22.1, 34.39, 17.66, 5.43, 2.65, 1.61, 0.91, 0.42, 0.21, 0.1, 0.05, 0.02, 0.02, 0.01, 0.01],
  10: [0, 3.64, 3.6, 8.56, 22.2, 33.56, 16.79, 6.27, 2.64, 1.24, 0.61, 0.34, 0.26, 0.08, 0.05, 0.05, 0.03, 0.02, 0.01, 0.01, 0.04],
  11: [0, 3.55, 8.1, 11.59, 15.06, 18.23, 14.45, 10.42, 7.91, 5.08, 2.9, 1.35, 0.66, 0.35, 0.16, 0.09, 0.05, 0, 0, 0.03, 0.02],
  12: [0, 6.68, 7.16, 11.83, 18.44, 18.53, 12.98, 11.16, 6.19, 3.54, 1.69, 0.8, 0.46, 0.27, 0.17, 0.05, 0.02, 0.01, 0.01, 0.01],
  13: [0, 8.88, 6.48, 11.26, 16.45, 21.38, 18.43, 6.88, 4.1, 2.32, 1.47, 0.9, 0.35, 0.54, 0.25, 0.1, 0.14, 0.03, 0.02, 0.02],
};

export const tandemLoadDistributions = {
  4: [0, 0, 0, 5.88, 1.44, 1.94, 2.73, 3.63, 4.96, 7.95, 11.58, 14.2, 13.15, 10.73, 7.47, 5.08, 3.12, 1.87, 1.3, 0.76, 0.53, 0.52, 0.3, 0.21, 0.18, 0.11, 0.06, 0.04, 0.08, 0.01, 0.02, 0.1, 0.01, 0.02, 0.01],
  5: [0],
  6: [0, 0, 0, 5.28, 8.43, 10.83, 8.99, 7.72, 7.5, 6.76, 6.06, 5.71, 5.17, 4.52, 3.9, 3.21, 3.91, 2.12, 1.74, 1.44, 1.26, 1.01, 0.83, 0.71, 0.63, 0.49, 0.39, 0.32, 0.26, 0.19, 0.17, 0.13, 0.08, 0.06, 0.07, 0.04, 0.04, 0.02, 0.01],
  7: [0, 0, 0, 13.76, 6.72, 6.5, 3.46, 7.07, 4.83, 4.97, 4.58, 4.26, 3.85, 3.44, 6.03, 3.68, 2.98, 2.89, 2.54, 2.66, 2.5, 1.57, 1.53, 2.13, 1.89, 1.17, 1.07, 0.87, 0.81, 0.47, 0.49, 0.38, 0.24, 0.15, 0.16, 0.06, 0.13, 0.06, 0.06, 0.02, 0.02],
  8: [0, 0, 0, 18.93, 8.07, 11.17, 11.87, 10.53, 8.26, 6.78, 5.33, 4.13, 3.12, 2.34, 1.82, 1.58, 1.2, 1.05, 0.94, 0.56, 0.64, 0.28, 0.28, 0.41, 0.2, 0.14, 0.11, 0.06, 0.05, 0.03, 0.02, 0.06, 0.02, 0.02],
  9: [0, 0, 0, 2.78, 3.92, 6.52, 7.62, 7.75, 7.01, 5.83, 5.6, 5.17, 5.05, 5.28, 5.53, 6.13, 6.28, 5.67, 4.46, 3.16, 2.13, 1.41, 0.91, 0.59, 0.39, 0.26, 0.17, 0.11, 0.08, 0.05, 0.03, 0.02, 0.02, 0.02, 0.02, 0.01, 0.01, 0.01],
  10: [0, 0, 0, 2.45, 2.19, 3.65, 5.4, 6.9, 7.49, 6.99, 6.62, 6.26, 5.95, 6.16, 6.54, 6.24, 5.92, 4.99, 3.63, 2.79, 2.24, 1.69, 1.26, 1.54, 0.73, 0.57, 0.4, 0.38, 0.25, 0.16, 0.15, 0.09, 0.08, 0.06, 0.05, 0.11, 0.04, 0.01, 0.01, 0.01],
  11: [0, 0, 0, 7.93, 3.15, 5.21, 8.23, 8.88, 8.45, 7.08, 5.49, 5.14, 5.99, 5.73, 4.37, 6.58, 4.61, 4.48, 2.91, 1.83, 1.12, 0.84, 0.68, 0.32, 0.21, 0.21, 0.07, 0.13, 0.15, 0.09, 0.03, 0.06, 0.01, 0.01, 0.01],
  12: [0, 0, 0, 5.23, 1.75, 3.35, 5.89, 8.73, 8.38, 9.77, 10.84, 10.78, 7.24, 6.14, 4.93, 3.93, 3.09, 2.74, 1.73, 1.32, 1.07, 0.58, 0.51, 0.43, 0.22, 0.22, 0.23, 0.2, 0.12, 0.07, 0.19, 0.09, 0.04, 0.02, 0.04, 0.12, 0, 0, 0, 0.01],
  13: [0, 0, 0, 6.42, 3.85, 5.59, 5.67, 5.74, 5.54, 4.9, 4.5, 6.45, 4.77, 4.34, 5.63, 7.24, 4.69, 4.51, 3.93, 4.2, 3.22, 2.28, 1.77, 1.23, 0.85, 0.64, 0.39, 0.6, 0.26, 0.18, 0.08, 0.14, 0.07, 0.08, 0.03, 0.01, 0.04, 0.02, 0.04, 0.02, 0.08],
};


export const tridemLoadDistributions = {
  4: [0],
  5: [0],
  6: [0],
  7: [0, 0, 0, 0, 0, 0, 5.89, 2.18, 0, 3.32, 2.98, 0, 3.27, 4.26, 0, 4.48, 5.11, 0, 7.01, 6.77, 0, 7.21, 7.18, 0, 6.63, 5.84, 0, 6.2, 6.91, 0, 4.34, 2.94, 0, 2.13, 1.42, 0, 1.96, 0.63, 0, 0.46, 0.28, 0, 0.24, 0.12, 0, 0.09, 0.08, 0, 0.02, 0.02, 0, 0.03],
  8: [0],
  9: [0],
  10: [0, 0, 0, 0, 0, 0, 16.21, 9.51, 0, 7.3, 5.83, 0, 5.82, 5.03, 0, 4.99, 5.79, 0, 6.71, 7.41, 0, 6.41, 4.93, 0, 4.54, 2.82, 0, 1.79, 1.34, 0, 0.98, 0.6, 0, 0.52, 0.34, 0, 0.35, 0.22, 0, 0.17, 0.13, 0, 0.07, 0.07, 0, 0.04, 0.03, 0, 0.03, 0.01, 0, 0.01],
  11: [0, 0, 0, 0, 0, 0, 23.31, 20.89, 0, 15.88, 12, 0, 5.8, 2.61, 0, 2.08, 2.06, 0, 2.94, 1.1, 0, 2.98, 1.95, 0, 1.87, 0.72, 0, 1.27, 0.41, 0, 0.4, 0.16, 0, 0.99, 0.2, 0, 0.38],
  12: [0, 0, 0, 0, 0, 0, 13.28, 6.38, 0, 6.74, 6, 0, 4.37, 4.53, 0, 8.01, 5.61, 0, 6.25, 8.04, 0, 6.7, 6.08, 0, 3.48, 5.81, 0, 2.22, 0.98, 0, 0.89, 0.96, 0, 1.39, 0.38, 0, 0.11, 0.08, 0, 0.23, 0.2, 0, 0.41, 0.07, 0, 0.09, 0.35, 0, 0.03, 0.1, 0, 0.23],
  13: [0, 0, 0, 0, 0, 0, 10.86, 4.4, 0, 4.75, 4.04, 0, 3.02, 4.46, 0, 4.99, 3.82, 0, 6.51, 5.49, 0, 6.53, 5.19, 0, 6.32, 5.2, 0, 5.47, 4.68, 0, 2.39, 2.33, 0, 2.71, 1.22, 0, 1.1, 1.17, 0, 1.13, 0.55, 0, 0.68, 0.17, 0, 0.22, 0.3, 0, 0.07, 0.11, 0, 0.12],
};


/**
 * @param axleConfiguration: string, either 'single', 'tandem', or 'tridem'
 * @param vehiclesPctAadt: percent of AADT due to each class of vehicle, categories 4 through 13
 *
 * @returns outputDistNormed: Normalized distribution
 */
export const getNewLoadDistribution = (axleConfiguration: PrAxleConfigurationType, vehiclesPctAadt: number[]) => {
  let dist: { [index: number]: number[] };

  const vClasses = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

  if (axleConfiguration === PrAxleConfigurationType.SINGLE) {
    dist = singleLoadDistributions;
  } else if (axleConfiguration === PrAxleConfigurationType.TANDEM) {
    dist = tandemLoadDistributions;
  } else if (axleConfiguration === PrAxleConfigurationType.TRIDEM) {
    dist = tridemLoadDistributions;
  }

  const outputDist: number[] = [];

  // Calculate for 0 - 110 kip
  const weights: number[] = [];
  for (let wt_kip = 0; wt_kip <= 110; wt_kip += 2) {
    weights.push(wt_kip);
  }

  // Create output distribution w/ zeros
  weights.forEach((wt, i) => {
    outputDist.push(0);
  });

  // For each class, sum across load spectra
  vClasses.forEach((k, i) => {
    const classPct = vehiclesPctAadt[i] / 100;

    // console.log(axleConfiguration, 'Class', k, classPct);

    weights.forEach((wt, j) => {
      if (Object.keys(dist).includes(k.toString())) {
        // console.log('Distribution', k, j, dist[k][j] / 100);
        outputDist[j] += classPct * dist[k][j] || 0;
      }
    });
  });

  const distSum = outputDist.reduce((a, b) => a + b, 0);
  const outputDistNormed = outputDist.map((pct) => (pct / distSum) * 100);

  return outputDistNormed;
};
